import { fraction, rateFor, toNet, toNumber } from "../../shared/money";
import { plural } from "../../shared/words";
import { type BundleRow, inUse, isProduct, type ItemPricing, type MaterialRow, type PriceListData, type PriceListSettings, type SupplierPriceRow, usesGroup } from "./meta";
import { oneOnlyClash, smartName } from "./smartTags";
import { type ItemRate, itemProfit, missingCost, type NeedsCheck, needsCheck, pricedAs, type Profit, underMinimum } from "./pricing";
import { itemNumberIssues, sharedNames } from "./setup";
import { ageOf, hasAmount } from "./supplierPricing";

/*
 * What needs attention on an item, a bundle or a supplier, the same everywhere it's shown: its
 * window's "Needs attention" box, its list's mark, tint and filters, and Home. Used by the server
 * (Home) and the screens alike, so they can't disagree.
 */

type NumberIssues = ReturnType<typeof itemNumberIssues>;

// ---------------------------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------------------------

/** Each thing that can need attention on an item (its list's filters are these too). */
export type ItemProblemId =
    | "servicem8Price"
    // A product only: items built from it aren't what their products add up to now (catalogue.ts: they never change by themselves).
    | "itemsToUpdate"
    // A product only: its group asks it for a tag of a set it has none of (productTags.ts).
    | "noNumber"
    | "sharedNumber"
    | "unknown"
    | "sharedName"
    | "low"
    | "changed"
    | "check"
    | "costUpdate"
    | "pricesDue"
    | "noCost"
    | "names"
    | "tagClash"
    // In a bundle only (listAttention `archived`): it can't be sold (an item on its own being archived is nothing to look at).
    | "archived"
    // In a bundle only (bundleItemAttention): hidden (on standby), so archived in ServiceM8, which makes it active again once the bundle's used
    // (README). Better as a product an item is built from (catalogue.ts).
    | "hiddenInBundle";

/**
 * Problems told from what only See costs shows (an item's pricing and groups, its costs, its supplier prices): without it, the Price
 * List hides them (costs.ts, MaterialsList's cost filters), so neither does Home count them.
 */
export const COST_PROBLEMS: ReadonlySet<ItemProblemId> = new Set(["low", "check", "costUpdate", "itemsToUpdate", "pricesDue", "noCost", "names"]);

export interface ItemProblem {
    id: ItemProblemId;
    /** A few words, for a list's mark ("No item number"). */
    text: string;
    /** Red, not amber: it can trip up accounting software (its item number) or loses money. */
    danger: boolean;
}

/** What the item checks need from the Price List's data: the screens' data, or the server's read of it. */
export type ItemData = Pick<
    PriceListData,
    "materials" | "bundles" | "unknownItems" | "taxRates" | "itemPricing" | "groups" | "nameTags" | "settings" | "costUpdates" | "supplierPrices" | "costFollows" | "tags" | "tagSets" | "itemTags" | "packages"
> & {
    changedInServiceM8: Pick<PriceListData["changedInServiceM8"], "material">;
};

/** What every item's checks share, worked out once (item numbers across everything, and lookups). */
export interface ItemContext {
    data: ItemData;
    numbers: NumberIssues;
    /** Active items sharing their name with another (setup.ts sharedNames): uuid -> the others. */
    names: ReadonlyMap<string, readonly MaterialRow[]>;
    unknown: ReadonlySet<string>;
    changed: ReadonlySet<string>;
    groups: ReadonlyMap<string, PriceListData["groups"][number]>;
    /** Items whose supplier prices (with an amount) are due a check or out of date (Price List settings): how many of each. */
    pricesDue: ReadonlyMap<string, PricesDue>;
    /** Every item, by uuid (a bundle's are looked up by it). */
    items: ReadonlyMap<string, MaterialRow>;
    /** Each product's built items (packages.ts): product uuid -> the items' uuids. */
    builtOf: ReadonlyMap<string, readonly string[]>;
    /** Each item as checked (assessItem), once per data load: a bundle's checks ask for its items' again. */
    assessed: Map<string, ItemAssessment>;
}

// One per data load: the same data asked again (each row, each warning) isn't worked out again.
const contexts = new WeakMap<object, ItemContext>();

/**
 * Each item's supplier prices (with an amount) due a check or out of date, in one pass (only items with some): only while its cost
 * follows them (a typed cost doesn't depend on them).
 */
function pricesDueByItem(data: Pick<ItemData, "supplierPrices" | "settings" | "costFollows">): Map<string, PricesDue> {
    const out = new Map<string, PricesDue>();
    const following = new Set(data.costFollows);
    for (const p of data.supplierPrices) {
        if (!hasAmount(p) || !following.has(p.materialUuid)) continue;
        const age = ageOf(p.checkedAt, data.settings);
        if (age === "current") continue;
        const was = out.get(p.materialUuid) ?? { due: 0, outdated: 0 };
        out.set(p.materialUuid, { due: was.due + 1, outdated: was.outdated + (age === "outdated" ? 1 : 0) });
    }
    return out;
}

/**
 * Problems that don't tint an item's row in a list (its other marks say so): supplier prices to check, marked by its cost (a
 * Materials list's Cost column) and in its needs-attention mark, as they're for its suppliers more than for it.
 */
export const tintsRow = (p: ItemProblem) => p.id !== "pricesDue";

/** "Supplier price to check" (or prices): its pill and its list's mark, short. */
export const pricesDueText = (due: PricesDue) => (due.due === 1 ? "Supplier price to check" : "Supplier prices to check");

/** In full, on hover: "2 of its supplier prices need checking (1 out of date): its cost follows them." */
export function pricesDueDetail(due: PricesDue) {
    const which = due.due === 1 ? (due.outdated ? "Its supplier price is out of date" : "Its supplier price is due a check") : `${due.due} of its supplier prices need checking${due.outdated ? ` (${due.outdated} out of date)` : ""}`;
    return `${which}: its cost follows ${due.due === 1 ? "it" : "them"}.`;
}

/** The shared part of the item checks for this data (kept while the data is the same object). */
/**
 * Item numbers missing or shared: items' among items and bundles (ServiceM8's rule); products' among products (catalogue.ts: theirs
 * are their own, one can be an item's too, and optional).
 */
function numberIssues(data: ItemData) {
    const items = itemNumberIssues([...data.materials.filter((m) => !isProduct(m)), ...data.bundles]);
    const products = itemNumberIssues(data.materials.filter(isProduct));
    return { missing: items.missing, clashes: new Map([...items.clashes, ...products.clashes]) };
}

export function itemContext(data: ItemData): ItemContext {
    const known = contexts.get(data);
    if (known) return known;
    const ctx: ItemContext = {
        data,
        numbers: numberIssues(data),
        names: new Map([...sharedNames(data.materials.filter((m) => !isProduct(m))), ...sharedNames(data.materials.filter(isProduct))]),
        unknown: new Set(data.unknownItems),
        changed: new Set(data.changedInServiceM8.material),
        groups: new Map(data.groups.map((g) => [g.id, g])),
        pricesDue: pricesDueByItem(data),
        items: new Map(data.materials.map((m) => [m.uuid, m])),
        builtOf: builtOfProducts(data.packages),
        assessed: new Map(),
    };
    contexts.set(data, ctx);
    return ctx;
}

function builtOfProducts(packages: ItemData["packages"]) {
    const out = new Map<string, string[]>();
    for (const [item, parts] of Object.entries(packages)) for (const p of parts) out.set(p.uuid, [...(out.get(p.uuid) ?? []), item]);
    return out;
}

/** A built item not what its products add up to now: its price to update (needsCheck), its cost (costUpdates: null if it's right), or both. */
export interface BuiltToUpdate {
    item: MaterialRow;
    check: NeedsCheck | null;
    cost: string | null;
}

/** The items built from this product (in use) that aren't what their products add up to now. */
export function builtToUpdate(product: Pick<MaterialRow, "uuid">, ctx: ItemContext): BuiltToUpdate[] {
    const out: BuiltToUpdate[] = [];
    for (const uuid of ctx.builtOf.get(product.uuid) ?? []) {
        const m = ctx.items.get(uuid);
        if (!m || !inUse(m)) continue;
        const check = needsCheck(m, rateOfItem(m, ctx), ctx.data.itemPricing[uuid]);
        const cost = ctx.data.costUpdates[uuid] ?? null;
        if (check || cost !== null) out.push({ item: m, check, cost });
    }
    return out;
}

/** An item's tax rate (its own, else the default), as its pricing checks take it. */
const rateOfItem = (m: Pick<MaterialRow, "tax_rate_uuid">, ctx: ItemContext): ItemRate => {
    const rate = rateFor(m.tax_rate_uuid, ctx.data.taxRates);
    return { uuid: rate?.uuid ?? "", tax: fraction(rate) };
};

/** An item, checked: what needs attention (worst first), and the facts behind it that lists show. */
export interface ItemAssessment {
    problems: ItemProblem[];
    /** Its item number: none, or shared with another item or bundle (active items only). */
    numberIssue: "missing" | "duplicate" | null;
    /** Its pricing gives another price now (active items only). */
    check: NeedsCheck | null;
    /** What it makes on its cost, as priced now and costed as estimated (pricing.ts itemProfit). */
    profit: Profit;
    /** Its name reads differently from what its tags give it (active items only). */
    nameToUpdate: boolean;
}

/**
 * Everything that needs attention on an item, worst first: what can trip up accounting software
 * (its item number), what loses money, what's unknown or changed in ServiceM8, then tidying. A
 * retired item only needs looking at if it changed in ServiceM8 (e.g. archived there by mistake); a hidden one is in use.
 * Making too little isn't counted when the price update waiting for it would fix that (one problem).
 */
export function assessItem(item: MaterialRow, ctx: ItemContext): ItemAssessment {
    // The same item (as loaded) is checked once: lists and bundles ask again.
    const known = ctx.assessed.get(item.uuid);
    if (known && ctx.items.get(item.uuid) === item) return known;
    const assessed = assess(item, ctx);
    if (ctx.items.get(item.uuid) === item) ctx.assessed.set(item.uuid, assessed);
    return assessed;
}

function assess(item: MaterialRow, ctx: ItemContext): ItemAssessment {
    const m = item;
    const pricing = ctx.data.itemPricing[m.uuid];
    const rate = rateOfItem(m, ctx);
    const profit = itemProfit(ctx.data, item, rate.tax);
    const changed = ctx.changed.has(m.uuid);
    if (!inUse(m)) {
        const problems: ItemProblem[] = changed ? [{ id: "changed", text: "Changed in ServiceM8", danger: false }] : [];
        return { problems, numberIssue: null, check: null, profit, nameToUpdate: false };
    }
    const numberIssue = numberIssueOf(m.uuid, ctx);
    const check = needsCheck(m, rate, pricing);
    // A product's name is its own (its tags are what it is): never a name to update.
    const nameToUpdate = !isProduct(m) && smartName(ctx.data, m) !== null;
    const problems: ItemProblem[] = [];
    if (numberIssue === "missing") problems.push({ id: "noNumber", text: "No item number", danger: true });
    if (numberIssue === "duplicate") problems.push({ id: "sharedNumber", text: "Item number shared with another item", danger: true });
    if (ctx.unknown.has(m.uuid)) problems.push({ id: "unknown", text: "Unknown item from ServiceM8", danger: false });
    const low = makesTooLittle(item, rate, profit, check, ctx);
    if (low) problems.push({ id: "low", text: "Making too little", danger: true });
    const byServiceM8 = servicem8PriceProblem(pricing);
    if (byServiceM8) problems.push(byServiceM8);
    if (changed) problems.push({ id: "changed", text: "Changed in ServiceM8", danger: false });
    if (check) problems.push({ id: "check", text: "Price to update", danger: false });
    // Its supplier price gives another cost now: never changed by itself (supplierPrices.ts costUpdates).
    if (m.uuid in ctx.data.costUpdates) problems.push({ id: "costUpdate", text: "Cost to update", danger: false });
    // A product changed since items were built from it: they're updated from it, or each on its own.
    const toUpdate = isProduct(m) ? builtToUpdate(m, ctx).length : 0;
    if (toUpdate) problems.push({ id: "itemsToUpdate", text: toUpdate === 1 ? "1 item to update" : `${toUpdate} items to update`, danger: false });
    // Any of its supplier prices due a check, or out of date: amber (its cost may be out of step; its suppliers say so too).
    const due = ctx.pricesDue.get(m.uuid);
    if (due) problems.push({ id: "pricesDue", text: pricesDueText(due), danger: false });
    if (missingCost(m, pricing)) problems.push({ id: "noCost", text: "No cost (its pricing needs one)", danger: false });
    if (ctx.names.has(m.uuid)) problems.push({ id: "sharedName", text: "Name shared with another item", danger: false });
    if (nameToUpdate) problems.push({ id: "names", text: "Name to update (its tags)", danger: false });
    // A pick-one set it has more than one tag of (moved in, or switched to pick one, before that was checked): take one off.
    const clash = oneOnlyClash(ctx.data, m.uuid);
    if (clash) problems.push({ id: "tagClash", text: `More than one ${clash.name} tag`, danger: false });
    return { problems, numberIssue, check, profit, nameToUpdate };
}

/** Built from products, but ServiceM8 prices it (servicem8Pricing.ts): not what they add up to. Keeping its price makes it a plain item. */
function servicem8PriceProblem(pricing: ItemPricing | undefined): ItemProblem | null {
    return pricing?.pricedInServiceM8 && pricing.fromParts ? { id: "servicem8Price", text: "Priced by ServiceM8, not its products", danger: false } : null;
}

/** Its item number: none, or shared with another item or bundle. */
function numberIssueOf(uuid: string, ctx: ItemContext): ItemAssessment["numberIssue"] {
    if (ctx.numbers.missing.has(uuid)) return "missing";
    return ctx.numbers.clashes.has(uuid) ? "duplicate" : null;
}


/** What needs attention on an item (assessItem's problems). */
export const itemProblems = (m: MaterialRow, ctx: ItemContext) => assessItem(m, ctx).problems;

/**
 * For a group's products, a price, cost or name to update is its own business (shown in its list, updated from its bar), not something
 * wrong; but a price to update by a product's own pricing (overriding the group's) isn't the group's.
 */
const beyondGroup = (problems: readonly ItemProblem[], pricing: ItemPricing | undefined) =>
    problems.filter((p) => p.id !== "names" && p.id !== "costUpdate" && (p.id !== "check" || !usesGroup(pricing)));

/**
 * A group's items that need attention (the same checks as everywhere: assessItem), each with its problems: `problems` without
 * its own updates (a price, cost or name to update: its rows show those), for its rows' marks and its Items tab's; `all`, every one,
 * for its Needs attention chips and narrowing its list (ProblemChips.tsx). Red when any is.
 */
export function groupAttention(data: ItemData, groupId: string) {
    const ctx = itemContext(data);
    const all = listAttention(
        data.materials.filter((m) => data.itemPricing[m.uuid]?.groupId === groupId).map((m) => m.uuid),
        ctx,
    );
    const problems = new Map<string, ItemProblem[]>();
    for (const [uuid, list] of all) {
        const found = beyondGroup(list, data.itemPricing[uuid]);
        if (found.length) problems.set(uuid, found);
    }
    return { problems, all, count: problems.size, danger: [...problems.values()].some((list) => list.some((p) => p.danger)) };
}

/** Every group's items needing attention, in one pass: how many, and whether any is red (a group none of whose items do isn't there). */
export function attentionByGroup(data: ItemData): Map<string, { count: number; danger: boolean }> {
    const ctx = itemContext(data);
    const out = new Map<string, { count: number; danger: boolean }>();
    for (const m of data.materials) {
        const pricing = data.itemPricing[m.uuid];
        const groupId = pricing?.groupId;
        if (!groupId) continue;
        const found = beyondGroup(assessItem(m, ctx).problems, pricing);
        if (!found.length) continue;
        const was = out.get(groupId) ?? { count: 0, danger: false };
        out.set(groupId, { count: was.count + 1, danger: was.danger || found.some((p) => p.danger) });
    }
    return out;
}

/**
 * How many of a group's products' prices don't match it yet, costs to update (their supplier prices moved: none without See costs),
 * and how many it can't price (no cost: pricing.ts missingCost). Its own business, shown in its list and window (and counted beside
 * Groups in the sidebar). Not names: only products are in groups, and a product's name is its own (its tags don't make it).
 */
export function groupOutOfDate(data: Pick<ItemData, "materials" | "itemPricing" | "taxRates" | "costUpdates">, groupId: string) {
    const members = data.materials.filter((m) => data.itemPricing[m.uuid]?.groupId === groupId);
    return {
        prices: members.filter((m) => {
            const rate = rateFor(m.tax_rate_uuid, data.taxRates);
            return needsCheck(m, { uuid: rate?.uuid ?? "", tax: fraction(rate) }, data.itemPricing[m.uuid]);
        }).length,
        noCost: members.filter((m) => missingCost(m, data.itemPricing[m.uuid])).length,
        costs: members.filter((m) => m.uuid in data.costUpdates).length,
    };
}

/**
 * The groups that need attention (the Groups list's Needs attention, all of it): any of their products' warnings, or their own
 * prices or costs to update, or products with no cost; each, and whether it's red (a product's warning is).
 */
export function groupsNeedingAttention(data: ItemData): Map<string, { danger: boolean }> {
    const attention = attentionByGroup(data);
    const out = new Map<string, { danger: boolean }>();
    for (const g of data.groups) {
        const stale = groupOutOfDate(data, g.id);
        const own = attention.get(g.id);
        if (own || stale.prices + stale.noCost + stale.costs > 0) out.set(g.id, { danger: own?.danger ?? false });
    }
    return out;
}

/** "3 items need attention". */
export const attentionText = (count: number) => `${count.toLocaleString()} ${count === 1 ? "item needs" : "items need"} attention`;

/**
 * Under the minimum markup on its cost, unless the price update waiting for it (its check) would fix that: taxed as it will be,
 * on its estimated cost (as everywhere: pricing.ts itemProfit; its cost to update is its own step).
 */
function makesTooLittle(m: MaterialRow, rate: ItemRate, profit: Profit, check: NeedsCheck | null, ctx: ItemContext) {
    const { settings } = ctx.data;
    if (profit.profitNet === null || !underMinimum(profit.markupPct, settings.minMarkup)) return false;
    if (!check) return true;
    const after = pricedAs(m, rate, ctx.data.itemPricing[m.uuid]);
    return underMinimum(itemProfit(ctx.data, { ...after.item, price: check.target }, after.tax, { was: m }).markupPct, settings.minMarkup);
}

// ---------------------------------------------------------------------------------------------
// Bundles and suppliers
// ---------------------------------------------------------------------------------------------

/** What a bundle's checks need of an item. */
export type BundleItem = Pick<MaterialRow, "name" | "active" | "hidden">;

/** Items in a bundle that are retired or gone (by name; "a deleted item" for one no longer here). Hidden ones are marked on their own (bundleItemAttention). */
export function retiredInBundle(b: Pick<BundleRow, "material_list">, items: ReadonlyMap<string, BundleItem>): string[] {
    return b.material_list.flatMap((l) => {
        const m = items.get(l.uuid);
        if (!m) return ["a deleted item"];
        return inUse(m) ? [] : [m.name];
    });
}

/**
 * What's wrong with a bundle, a few words each (worst first): its item number, making too little (`low`: bundleMakesTooLittle),
 * retired items in it, its items needing attention (`itemsNeedingAttention`: bundleItemAttention), changes from ServiceM8, and
 * (`nameToUpdate`) its name not reading as its tags give it.
 */
export function bundleProblems(
    b: Pick<BundleRow, "uuid" | "material_list" | "active">,
    numbers: NumberIssues,
    items: ReadonlyMap<string, BundleItem>,
    changedInServiceM8: boolean,
    nameToUpdate = false,
    opts: { low?: boolean; itemsNeedingAttention?: number } = {},
): string[] {
    const problems: string[] = [];
    if (b.active && numbers.missing.has(b.uuid)) problems.push("No item number");
    if (b.active && numbers.clashes.has(b.uuid)) problems.push("Item number shared with another item");
    if (b.active && opts.low) problems.push("Making too little");
    const retired = b.active ? retiredInBundle(b, items) : [];
    if (retired.length) problems.push(`${plural(retired.length, "archived item")} in it`);
    if (b.active && opts.itemsNeedingAttention) problems.push(attentionText(opts.itemsNeedingAttention));
    if (changedInServiceM8) problems.push("Changed in ServiceM8");
    if (b.active && nameToUpdate) problems.push("Name to update");
    return problems;
}

/**
 * Items listed together (a bundle's, a group's) that need attention, each with its problems: every one (assessItem), so a list
 * shows whatever there is to look at on its items. `seesCosts` false: those only See costs tells (COST_PROBLEMS) are left out, as in
 * the lists. `archived`: a retired item is one too (a bundle's: it can't be sold). In the order given; each once.
 */
export function listAttention(uuids: Iterable<string>, ctx: ItemContext, seesCosts = true, archived = false): Map<string, ItemProblem[]> {
    const out = new Map<string, ItemProblem[]>();
    for (const uuid of uuids) {
        const m = ctx.items.get(uuid);
        if (!m || out.has(uuid)) continue;
        const problems = assessItem(m, ctx).problems.filter((p) => seesCosts || !COST_PROBLEMS.has(p.id));
        if (archived && !inUse(m)) problems.unshift({ id: "archived", text: "Archived", danger: false });
        if (problems.length) out.set(uuid, problems);
    }
    return out;
}

/**
 * A bundle's items that need attention (listAttention): anything to look at on an item is something to look at on a bundle selling
 * it, and an archived one in it can't be sold. `archived` false: not those (a list saying them on their own: bundleProblems). A
 * hidden one (on standby) comes back on jobs once the bundle's used (ServiceM8 makes it active again): take it out, or unhide it.
 */
export function bundleItemAttention(b: Pick<BundleRow, "material_list">, ctx: ItemContext, seesCosts = true, archived = true): Map<string, ItemProblem[]> {
    const out = listAttention(
        b.material_list.map((l) => l.uuid),
        ctx,
        seesCosts,
        archived,
    );
    for (const l of b.material_list) {
        const m = ctx.items.get(l.uuid);
        if (!m) continue;
        const own: ItemProblem[] = [];
        if (m.hidden) own.push({ id: "hiddenInBundle", text: "Hidden", danger: false });
        if (own.length) out.set(m.uuid, [...own, ...(out.get(m.uuid) ?? []).filter((p) => !own.some((o) => o.id === p.id))]);
    }
    return out;
}

/**
 * What a bundle makes on its items' costs, as a markup (before tax, each item at its own price and cost, as ServiceM8 adds them).
 * Null: nothing to tell it from (no cost, or an item in it with none: it'd be far too high).
 */
export function bundleMarkup(b: Pick<BundleRow, "material_list">, ctx: ItemContext): number | null {
    let priceNet = 0;
    let costNet = 0;
    for (const l of b.material_list) {
        const m = ctx.items.get(l.uuid);
        if (!m) continue;
        if (toNumber(m.cost) <= 0) return null;
        const { tax } = rateOfItem(m, ctx);
        priceNet += toNet(toNumber(m.price), m.price_includes_taxes, tax) * l.quantity;
        costNet += toNet(toNumber(m.cost), m.price_includes_taxes, tax) * l.quantity;
    }
    return costNet > 0 ? ((priceNet - costNet) / costNet) * 100 : null;
}

/** An active bundle whose markup is under the minimum (Price List settings), or that makes nothing or a loss: red, as an item's. */
export const bundleMakesTooLittle = (b: Pick<BundleRow, "material_list" | "active">, ctx: ItemContext) => b.active && underMinimum(bundleMarkup(b, ctx), ctx.data.settings.minMarkup);

/** ""Red" also uses it. Every item needs its own." (or bundle): the others with the same item number, by name. */
export function sharedNumberText(others: readonly string[], noun: "item" | "bundle") {
    const names = others.map((n) => `"${n}"`).join(", ");
    return `${names} also ${others.length === 1 ? "uses" : "use"} it. Every ${noun} needs its own.`;
}

/** "Another item (DL2) has the same name.": the others with its name, by item number. */
export function sharedNameText(others: readonly Pick<MaterialRow, "name" | "item_number">[]) {
    const which = others.map((o) => (o.item_number ? o.item_number : "an item with no item number")).join(", ");
    return `${others.length === 1 ? "Another item" : `${others.length} other items`} (${which}) ${others.length === 1 ? "has" : "have"} the same name.`;
}

/** A supplier's prices (for active items) due for a recheck, and how many of those are out of date (past `outdatedDays`). */
export interface PricesDue {
    due: number;
    outdated: number;
}

/** Each supplier's prices due for a recheck, in one pass (only suppliers with some). */
export function pricesDueBySupplier(
    prices: readonly Pick<SupplierPriceRow, "supplierUuid" | "materialUuid" | "checkedAt">[],
    activeItems: ReadonlySet<string>,
    settings: Pick<PriceListSettings, "recheckDays" | "outdatedDays">,
): Map<string, PricesDue> {
    const bySupplier = new Map<string, PricesDue>();
    for (const p of prices) {
        if (!activeItems.has(p.materialUuid)) continue;
        const age = ageOf(p.checkedAt, settings);
        if (age === "current") continue;
        const counts = bySupplier.get(p.supplierUuid) ?? { due: 0, outdated: 0 };
        counts.due++;
        if (age === "outdated") counts.outdated++;
        bySupplier.set(p.supplierUuid, counts);
    }
    return bySupplier;
}

/** What's wrong with a supplier, a few words each: changes from ServiceM8, prices due for a recheck. */
export function supplierProblems(active: boolean, due: PricesDue | undefined, changedInServiceM8: boolean): string[] {
    const problems: string[] = [];
    if (changedInServiceM8) problems.push("Changed in ServiceM8");
    if (active && due?.due) {
        const outdated = due.outdated ? ` (${due.outdated.toLocaleString()} out of date)` : "";
        problems.push(`${plural(due.due, "price")} to recheck${outdated}`);
    }
    return problems;
}

/** Red, not amber: an active supplier with prices out of date (costs that may well be wrong). */
export const supplierUrgent = (active: boolean, due: PricesDue | undefined) => active && (due?.outdated ?? 0) > 0;

/** Red, not amber: an active bundle with no item number or a shared one (it can trip up accounting software). */
export const bundleNumberProblem = (b: Pick<BundleRow, "uuid" | "active">, numbers: NumberIssues) => b.active && (numbers.missing.has(b.uuid) || numbers.clashes.has(b.uuid));
